import { client, handleRequest, handleRequestBody } from "./client.ts";

export type DocumentCategory =
	| "projects"
	| "art_rrt"
	| "reports"
	| "contracts"
	| "permits"
	| "other";

export interface DocumentUploader {
	id: string;
	name: string;
}

export interface ConstructionDocument {
	id: string;
	title: string;
	file_name: string;
	category: DocumentCategory;
	content_type: string;
	size_bytes: number;
	client_visible: boolean;
	uploaded_by: DocumentUploader;
	created_at: string;
	updated_at: string;
}

export type DocumentCountsByCategory = Partial<
	Record<DocumentCategory, number>
>;

export interface ListDocumentsParams {
	category?: DocumentCategory;
	page?: number;
	pageSize?: number;
}

export interface ListDocumentsResponse {
	count: number;
	items: ConstructionDocument[];
	counts_by_category: DocumentCountsByCategory;
}

export interface UploadGrant {
	url: string;
	fields: Record<string, string>;
	expires_at: string;
}

export interface StartDocumentUploadInput {
	fileName: string;
	sizeBytes: number;
	category: DocumentCategory;
	title?: string;
	clientVisible?: boolean;
}

export interface StartDocumentUploadResponse {
	document: ConstructionDocument;
	upload: UploadGrant;
}

export interface UpdateDocumentInput {
	title?: string;
	category?: DocumentCategory;
	clientVisible?: boolean;
}

export type DocumentDisposition = "inline" | "attachment";

export interface DocumentFileURL {
	url: string;
	expires_at: string;
}

interface DocumentResponse {
	document: ConstructionDocument;
}

function documentsPath(constructionId: string, suffix = ""): string {
	return `/constructions/${constructionId}/documents${suffix}`;
}

export async function listDocuments(
	constructionId: string,
	params: ListDocumentsParams = {},
): Promise<ListDocumentsResponse> {
	const response = await handleRequestBody(() =>
		client.get<ListDocumentsResponse>(documentsPath(constructionId), {
			params: {
				category: params.category,
				page: params.page,
				page_size: params.pageSize,
			},
		}),
	);
	return {
		count: response.count ?? 0,
		items: response.items ?? [],
		counts_by_category: response.counts_by_category ?? {},
	};
}

export async function getDocument(
	constructionId: string,
	documentId: string,
): Promise<ConstructionDocument> {
	const { document } = await handleRequestBody(() =>
		client.get<DocumentResponse>(
			documentsPath(constructionId, `/${documentId}`),
		),
	);
	return document;
}

export function startDocumentUpload(
	constructionId: string,
	input: StartDocumentUploadInput,
): Promise<StartDocumentUploadResponse> {
	return handleRequestBody(() =>
		client.post<StartDocumentUploadResponse>(
			documentsPath(constructionId, "/uploads"),
			{
				file_name: input.fileName,
				size_bytes: input.sizeBytes,
				category: input.category,
				title: input.title || undefined,
				client_visible: input.clientVisible ?? false,
			},
		),
	);
}

export async function completeDocumentUpload(
	constructionId: string,
	documentId: string,
): Promise<ConstructionDocument> {
	const { document } = await handleRequestBody(() =>
		client.post<DocumentResponse>(
			documentsPath(constructionId, `/${documentId}/complete`),
		),
	);
	return document;
}

export async function updateDocument(
	constructionId: string,
	documentId: string,
	input: UpdateDocumentInput,
): Promise<ConstructionDocument> {
	const { document } = await handleRequestBody(() =>
		client.patch<DocumentResponse>(
			documentsPath(constructionId, `/${documentId}`),
			{
				title: input.title,
				category: input.category,
				client_visible: input.clientVisible,
			},
		),
	);
	return document;
}

export async function removeDocument(
	constructionId: string,
	documentId: string,
): Promise<void> {
	await handleRequest(() =>
		client.delete(documentsPath(constructionId, `/${documentId}`)),
	);
}

export function getDocumentFileURL(
	constructionId: string,
	documentId: string,
	disposition?: DocumentDisposition,
): Promise<DocumentFileURL> {
	return handleRequestBody(() =>
		client.get<DocumentFileURL>(
			documentsPath(constructionId, `/${documentId}/file`),
			{ params: { disposition } },
		),
	);
}

export interface StorageUploadProgress {
	loaded: number;
	total: number;
}

export class StorageUploadError extends Error {
	constructor(public status: number) {
		super(`storage upload failed with status ${status}`);
		this.name = "StorageUploadError";
	}
}

export function uploadToStorage(
	grant: UploadGrant,
	file: File,
	onProgress?: (progress: StorageUploadProgress) => void,
	signal?: AbortSignal,
): Promise<void> {
	return new Promise((resolve, reject) => {
		if (signal?.aborted) {
			reject(new DOMException("upload aborted", "AbortError"));
			return;
		}

		const form = new FormData();
		for (const [name, value] of Object.entries(grant.fields)) {
			form.append(name, value);
		}
		form.append("file", file);

		const xhr = new XMLHttpRequest();
		xhr.open("POST", grant.url);
		xhr.withCredentials = false;

		function onAbort() {
			xhr.abort();
		}

		function cleanup() {
			signal?.removeEventListener("abort", onAbort);
		}

		xhr.upload.onprogress = (event) => {
			onProgress?.({
				loaded: event.loaded,
				total: event.lengthComputable ? event.total : file.size,
			});
		};
		xhr.onload = () => {
			cleanup();
			if (xhr.status >= 200 && xhr.status < 300) {
				onProgress?.({ loaded: file.size, total: file.size });
				resolve();
				return;
			}
			reject(new StorageUploadError(xhr.status));
		};
		xhr.onerror = () => {
			cleanup();
			reject(new StorageUploadError(0));
		};
		xhr.onabort = () => {
			cleanup();
			reject(new DOMException("upload aborted", "AbortError"));
		};

		signal?.addEventListener("abort", onAbort);
		xhr.send(form);
	});
}
